import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";

import FmeaApi from "../../services/FmeaApi";
import ProductApi from "../../services/ProductApi";
import FmeaTypeEnum from "../../enum/FmeaTypeEnum";
import FmeaStatusEnum from "../../enum/FmeaStatusEnum";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const FmeaTypeLabels: Record<FmeaTypeEnum, string> = {
  [FmeaTypeEnum.AIAG_2008]: "AIAG 2008",
  [FmeaTypeEnum.AIAG_VDA_2019]: "AIAG/VDA 2019",
};

const typeOptions = Object.values(FmeaTypeEnum)
  .filter((value) => typeof value === "number")
  .map((value) => ({
    label: FmeaTypeLabels[value as FmeaTypeEnum],
    value: value as FmeaTypeEnum,
  }));

const statusOptions = Object.entries(FmeaStatusEnum)
  .filter(([k]) => !isNaN(+k))
  .map(([k, v]) => ({ label: String(v), value: +k }));

const EditFormFmea: React.FC = () => {
  const { fmeaId } = useParams<{ fmeaId: string }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translate } = useTranslation("form");

  const translatedStatusOptions = useMemo(() => statusOptions.map((opt) => ({
    ...opt,
    label: translate(opt.label.toLowerCase()),
  })), [translate]);

  const [products, setProducts] = useState<{ id: string; partNumber: string }[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>("");

  const [productionUnit, setProductionUnit] = useState("");
  const [area, setArea] = useState("");
  const [line, setLine] = useState("");
  const [subline, setSubline] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [typeFmea, setTypeFmea] = useState<FmeaTypeEnum | "">("");
  const [responsible, setResponsible] = useState("");
  const [status, setStatus] = useState<FmeaStatusEnum>(FmeaStatusEnum.Active);

  const [fmeaCode, setFmeaCode] = useState("");
  const [createdAt, setCreatedAt] = useState<Date | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const [loading, setLoading] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    ProductApi.GetAll()
      .then((r) => setProducts(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!fmeaId) return;
    setLoading(true);
    (async () => {
      try {
        const raw = await FmeaApi.GetById(fmeaId);
        const f = raw.data ?? raw;
        setTitle(f.title);
        setDescription(f.description);
        setSelectedProductId(f.productId || "");
        setResponsible(f.responsible);
        setProductionUnit(f.productionUnit);
        setArea(f.area);
        setLine(f.line);
        setSubline(f.subline);

        // Handling potential string/number enum mismatch from API
        const typeValue = typeof f.fmeaType === 'string' ? FmeaTypeEnum[f.fmeaType as keyof typeof FmeaTypeEnum] : f.fmeaType;
        const statusValue = (f.active === true || f.isActive === true || f.status === "Active") 
            ? FmeaStatusEnum.Active 
            : (f.status === "Inactive" || f.status === 0 || f.status === "0")
              ? FmeaStatusEnum.Inactive
              : (f.status as FmeaStatusEnum);

        setTypeFmea(typeValue as FmeaTypeEnum);
        setStatus(statusValue as FmeaStatusEnum);
        setFmeaCode(f.formattedCode || f.code || "");
        setCreatedAt(new Date(f.createdAt));
        setUpdatedAt(f.updatedAt ? new Date(f.updatedAt) : null);
      } catch (err: any) {
        displayToast("error", translate("errorLoading"), err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [fmeaId, displayToast, translate]);

  const handleSave = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();

    if (!title.trim() || !description.trim() || !responsible.trim()) {
      displayToast("warn", translate("warning"), translate("fillInAllRequiredFields"));
      return;
    }

    setLoading(true);
    try {
      await FmeaApi.Update({
        id: fmeaId!,
        title,
        description,
        responsible,
        fmeaType: typeFmea as number,
        status,
        productId: selectedProductId,
      });
      displayToast("success", translate("successEditFmea"), "", false, 3000);
      navigate(-1);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || translate("errorEditFmea");
      displayToast("error", translate("errorEditFmea"), msg, false, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FormCard
        title={translate("editFmea")}
        icon="edit"
        disabled={loading}
        onSubmit={handleSave}
        onCancel={() => setShowConfirmCancel(true)}
        submitButtonLabel={translate("save")}
        cancelButtonLabel={translate("Cancel")}
        numColumns={12}
      >
        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="text"
            label={translate("code")}
            value={fmeaCode ? `FMEA-${String(fmeaCode).padStart(3, "0")}` : ""}
            disabled
            onChange={() => {}}
          />
        </div>

        <div style={{ gridColumn: "span 12" }}>
          <Input
            type="text"
            label={translate("title")}
            value={title}
            required
            maxLength={100}
            onChange={(val: any) => setTitle(val)}
          />
        </div>

        <div style={{ gridColumn: "span 12" }}>
          <Input
            type="textArea"
            label={translate("description")}
            value={description}
            required
            rows={3}
            maxLength={500}
            onChange={(val: any) => setDescription(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="select"
            label={translate("fmeaType")}
            value={typeFmea}
            disabled
            options={typeOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(val: any) => setTypeFmea(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="text"
            label={translate("responsible")}
            value={responsible}
            required
            onChange={(val: any) => setResponsible(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="text"
            label={translate("productionUnit")}
            value={productionUnit}
            disabled
            onChange={() => {}}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="text"
            label={translate("area")}
            value={area}
            disabled
            onChange={() => {}}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="text"
            label={translate("line")}
            value={line}
            disabled
            onChange={() => {}}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="text"
            label={translate("subline")}
            value={subline}
            disabled
            onChange={() => {}}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="select"
            label={translate("product")}
            value={selectedProductId}
            disabled
            options={products.map(p => ({ label: p.partNumber, value: p.id }))}
            optionLabel="label"
            optionValue="value"
            onChange={() => {}}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="select"
            label={translate("status")}
            value={status}
            options={translatedStatusOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(val: any) => setStatus(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="calendar"
            label={translate("createdAt")}
            value={createdAt}
            disabled
            onChange={() => {}}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="calendar"
            label={translate("updatedAt")}
            value={updatedAt}
            disabled
            onChange={() => {}}
          />
        </div>

        <ConfirmModal
          visible={showConfirmCancel}
          onHide={() => setShowConfirmCancel(false)}
          onConfirm={() => navigate(-1)}
        />
      </FormCard>
    </>
  );
};

export default EditFormFmea;
